import { config } from './config'
import { createUrl } from './create-url'

// 汎用メソッド群を宣言

export const methods = {
  /**
   * 都道府県からエリア名を取得
   *
   * @param {string} prefecture
   * @returns {string} name
   */
  getArea(prefecture) {
    return config.searchArea.find((area) => {
      return area.prefectures.includes(prefecture)
    }).name
  },
  /**
   * 都道府県からエリア・都道府県・市区町村を取得
   *
   * @param {object} facility
   * @returns {object} {area, facility, city}
   */
  getAreaLabel(facility) {
    if (!facility.prefecture) return []
    const area = this.getArea(facility.prefecture)
    const facilityType = this.getFacilityType(facility.facility_type)
    return {
      area,
      prefecture: facility.prefecture,
      city: facility.city,
      facilityType
    }
  },
  /**
   * 施設タイプのコードから施設タイプ名を取得
   * @param {int} facilityType
   * @returns {string} 施設名
   */
  getFacilityType(facilityType) {
    return config.facilityTypeList.find((type) => type.code === facilityType)
      .label
  },
  /**
   * エリアのリストを返す
   * [{ name: '関東', value: '関東' }]
   *
   * @returns {object} [{name, value}]
   */
  getAreaList() {
    return config.searchArea.map((area) => {
      return { name: area.name, value: area.name }
    })
  },

  /**
   * 都道府県のリストを返す
   * [{ name: '東京都', value: '東京都' }]
   *
   * @returns {object} [{name, prefecture, value}]
   */
  getPrefectureList() {
    const prefectureList = []
    // 都道府県の配列を生成
    config.searchArea.forEach((area) => {
      prefectureList.push(...area.prefectures)
    })
    // [{ name: '北海道', value: '北海道' }] 形式の配列に変更
    return prefectureList.map((prefecture) => {
      return { name: prefecture, value: prefecture }
    })
  },

  /**
   * エリアモードを返す
   *
   * @param {*} facet
   * @returns {string} mode
   */
  getAreaMode(facet) {
    let mode = 'area'
    if (facet.city && facet.city.length) {
      mode = 'city'
    } else if (facet.prefecture && facet.prefecture.length) {
      mode = 'prefecture'
    }
    return mode
  },

  /**
   * エリアのfacet情報を、プルダウンの選択肢用配列に変換
   * [{ '東京都': 166 }]
   * ↓↓↓
   * [{ name: '東京都', value: '東京都' cnt: 166 }]
   *
   * @param {*} facet
   * @returns {object} {name, value, cnt}
   */
  getAreaOptions(facetArea) {
    if (!facetArea) return []

    const options = facetArea.map((facet) => {
      const values = Object.entries(facet)[0]
      return { name: values[0], value: values[0], cnt: values[1] }
    })

    // 件数を1件以上に限定して返す
    return options.filter((option) => option.cnt > 0)
  },

  getStationOptions(facetStation) {
    if (!facetStation) return []
    const options = facetStation.map((facet) => {
      const values = Object.entries(facet)[0]
      return { name: values[0], value: values[0], cnt: values[1] }
    })

    // 件数を1件以上に限定して返す
    return options.filter((option) => option.cnt > 0)
  },

  /**
   * イベント規模・種類マスタを単一配列に変換
   * config.facilityEventTypesを整形
   * [{ type: { label: '大', value: 11, title: '大きな会議' } }]
   * ↓↓↓
   * [{ label: '大', name: '大きな会議', value: 11, searchTitle: '大人数向け会議室', searchSubTitle: '80人・100人以上のセミナー会場', title: '大きな会議', eventType: 0 }]
   *
   * @returns {object} [{label, name, value, searchTitle, title, eventType}]
   */
  getFacilityEventTypesList() {
    const facilityEventTypesList = []

    config.facilityEventTypes.forEach((facilityEventType, index) => {
      const eventTypesList = facilityEventType.types.map((type) => {
        return {
          label: type.label,
          name: type.title,
          value: type.value.toString(),
          searchTitle: type.searchTitle,
          searchSubTitle: type.searchSubTitle,
          title: type.title,
          eventType: index
        }
      })
      facilityEventTypesList.push(...eventTypesList)
    })

    return facilityEventTypesList
  },

  /**
   * eventTypeのfacet情報を、チェックボックスの選択肢用配列に変換
   * [{ url: '/s/pr-東京都/sc-11,12/', code: 11, active: false }]
   * ↓↓↓
   * [{ url: '/s/pr-東京都/sc-11,12/', code: 11, value: 11, active: false, name: '大', title: '大きな会議' }]
   *
   * @param {array} facetEventType
   * @returns {object} [{url, code, value, active, name, title}]
   */
  getEventTypeOptions(facetEventType = []) {
    const eventTypesList = this.getFacilityEventTypesList()

    const options = config.facilityEventTypes.map(() => [])
    facetEventType.forEach((eventType) => {
      const target = eventTypesList.find(
        (facilityEventType) =>
          facilityEventType.value === eventType.code.toString()
      )
      if (target) {
        options[target.eventType].push({
          url: eventType.url,
          code: eventType.code,
          value: eventType.code,
          active: eventType.active,
          name: target.label,
          title: target.title,
          searchTitle: target.searchTitle + '【' + target.searchSubTitle + '】'
        })
      }
    })
    return options
  },

  /**
   * 設備・サービスマスタを単一配列に変換
   * config.serviceListを整形
   * [{ code: 1, name: 'tatami', label: '和室' }]
   * ↓↓↓
   * [{ name: '和室', value: '1' }]
   *
   * @returns {object} {name,value}
   */
  getServiceList() {
    return config.serviceList.map((service) => {
      return {
        name: service.label,
        value: service.code.toString(),
        title: service.title
      }
    })
  },

  /**
   * serviceのfacet情報を、チェックボックスの選択肢用配列に変換
   * [{ url: '/s/pr-東京都/sv-1,2/', code: 1, active: false }]
   * ↓↓↓
   * [{ url: '/s/pr-東京都/sv-1,2/', code: 1, value: 1, active: false, name: '大' }]
   *
   * @param {array} facetService
   * @returns {object} [{url, code, value, active, name}]
   */
  getServiceOptions(facetService = []) {
    const serviceList = this.getServiceList()
    return facetService.map((service) => {
      return {
        url: service.url,
        code: service.code,
        value: service.code,
        active: service.active,
        // name値を含める
        name: serviceList.find(
          (master) => master.value === service.code.toString()
        ).name
      }
    })
  },

  /**
   *  施設タイプマスタを単一配列に変換
   *
   * @returns {object} {name, value}
   */
  getFacilityTypeList() {
    return config.facilityTypeList.map((service) => {
      return { name: service.label, value: service.code.toString() }
    })
  },
  /**
   * facilityTypeのfacet情報を、チェックボックスの選択肢用配列に変換
   *
   * @param {array} facetFacilityType
   * @returns {object} {url, code, value, active, name}
   */
  getFacilityTypeOptions(facetFacilityType = []) {
    const facilityTypeList = this.getFacilityTypeList()
    return facetFacilityType.map((facilityType) => {
      return {
        url: facilityType.url,
        code: facilityType.code,
        value: facilityType.code,
        active: facilityType.active,
        // name値を含める
        name: facilityTypeList.find(
          (master) => master.value === facilityType.code.toString()
        ).name
      }
    })
  },

  /**
   * イベント規模・種類の配列を整形
   * [1,2] => [{name: "大", code: 11, title: '大人数向け会議室【80人・100人以上のセミナー会場】'}]
   *
   * @param {array} eventType
   * @returns {object} [{name, code}]
   */
  formatFacilityEventTypesList(eventType = []) {
    const facilityEventTypesList = []

    // イベント規模・種類マスタを整形
    config.facilityEventTypes.forEach((facilityEventType, index) => {
      const eventTypesList = []
      // イベントの規模別に配列化
      facilityEventType.types.forEach((type) => {
        if (eventType.includes(type.value)) {
          eventTypesList.push({
            name: type.label,
            code: type.value,
            title: type.searchTitle + '【' + type.searchSubTitle + '】',
            label: facilityEventType.title
          })
        }
      })
      // イベントの規模配列を、イベントの種類別配列に代入
      facilityEventTypesList[index] = eventTypesList
    })

    // イベントの規模・種類の選択肢が存在する項目に限定
    return facilityEventTypesList.filter(
      (facilityEventType) => facilityEventType.length > 0
    )
  },

  /**
   * 設備・サービスアイコンの配列を整形
   * [11,12] => ['catering', 'coinlocker']
   *
   * @param {array} icon
   * @returns {array}
   */
  formatServiceList(icon = []) {
    const serviceNames = {}

    config.serviceList.forEach((service) => {
      if (icon.includes(service.code)) {
        serviceNames[service.code] = service.name
      }
    })
    if (Object.keys(serviceNames).length) {
      return icon.map((code) => serviceNames[code])
    } else {
      return []
    }
  },

  // 施設の住所を整形
  getFacilityAddress(detail) {
    return detail.prefecture + detail.address + ' ' + detail.address2
  },

  // 画像URLを整形
  // 例: /upload/facility/12/dd69522ffaa034c800938333baf84964_1280_960.jpg
  getPhotoUrl({ photo, id, width = 720, height = 540 }) {
    const pathInfo = photo.split('.')
    let imagePath = config.imageUploadPath
    /* istanbul ignore next */
    if (methods.isStubMode()) {
      imagePath = '/stub' + imagePath
    }
    imagePath = imagePath + id + '/'
    let name = 'noimage'
    let ext = 'png'
    if (pathInfo && pathInfo.length === 2) {
      name = pathInfo[0]
      ext = pathInfo[1]
    } else {
      imagePath = '/noimage/facility/'
    }
    return imagePath + name + '_' + width + '_' + height + '.' + ext
  },

  // 配列 → カンマ区切りに変換
  convertArrayToString(data) {
    if (!data) return ''
    return data.join(',')
  },

  // カンマ区切り -> 配列に変換
  convertStringToArray(data) {
    if (!data) return []
    return data.split(',')
  },

  // Stubモードか確認する
  isStubMode() {
    return process.env.API_STUB_FORCE === '1'
  },

  // APIBaseURLを返す
  getApiBaseUrl() {
    /* istanbul ignore next */
    if (methods.isStubMode()) {
      return methods.getApiStubBaseUrl()
    }
    return process.env.API_ENDPOINT + config.apiBaseUrl
  },

  // APIBaseURLを返す
  getApiStubBaseUrl() {
    return (process.env.API_STUB_ENDPOINT || '') + '/stub' + config.apiBaseUrl
  },

  // レビュー投稿URLを返す
  getWriteReviewUrl(placeId) {
    return 'https://search.google.com/local/writereview?placeid=' + placeId
  },

  // レビュー一覧URLを返す
  getReviewListUrl(placeId) {
    return 'https://search.google.com/local/reviews?placeid=' + placeId
  },

  // イベント規模ファセット情報 or 設備ファセット情報を整形
  // { "11": 100 }
  // ↓↓↓
  // [{ url: '/s/pr-東京都/sc-11,12/', code: 11, active: false }]
  formatFacet(targetFacet = [], query, fieldName) {
    const formatedFacet = []
    targetFacet.forEach((key) => {
      const facet = Object.entries(key)[0]
      // 件数を1件以上に限定
      if (facet[1] > 0) {
        // searchCondition の値を配列化
        const fieldNameValues = [...query[fieldName]]

        let active = false
        const idx = query[fieldName].indexOf(facet[0])
        if (idx === -1) {
          // 未選択の場合
          fieldNameValues.push(facet[0])
        } else {
          // 選択済みの場合
          active = true
          fieldNameValues.splice(idx, 1)
        }

        // searchCondition配列と結合するため、変数化
        const updatedValue = { [fieldName]: fieldNameValues, page: '' }

        formatedFacet.push({
          url: createUrl.createSearchUrl({ ...query, ...updatedValue }),
          code: Number(facet[0]),
          active
        })
      }
    })
    return formatedFacet
  },

  /**
   * facetをconfigの並びに合わせる
   *
   * @param {*} targetFacet
   * @param {*} masterList
   * @returns
   */
  sortFacet(targetFacet, masterList) {
    // マスタとマッチさせるため、codeのみの配列を宣言
    // [{ '12': 11 }, { '91': 4 }] >> ['12', '91']
    const targetCodes = targetFacet.map((facet) => Object.keys(facet)[0])
    const sortedFacet = []
    masterList.forEach((master) => {
      const idx = targetCodes.indexOf(master.value)
      if (idx !== -1) {
        sortedFacet.push(targetFacet[idx])
      }
    })
    return sortedFacet
  },

  /**
   * facet.cityを名前順に並べ替える
   *
   * @param {object} facet
   * @returns {object}
   */
  sortFacetCity(facet) {
    const cities = facet.map((city) => city)
    cities.sort((a, b) => {
      if (Object.keys(a)[0] < Object.keys(b)[0]) {
        return -1
      } else {
        return 1
      }
    })
    return cities
  },
  sortFacetStation(facet) {
    const stations = facet.map((station) => station)
    stations.sort((a, b) => {
      if (Object.keys(a)[0] < Object.keys(b)[0]) {
        return -1
      } else {
        return 1
      }
    })
    return stations
  },
  /**
   * query.serviceをconfigの並びに合わせる
   *
   * @param {*} queryService
   * @returns
   */
  sortService(queryService) {
    const sortedService = []
    const queryServiceList = queryService.split(',')
    const serviceList = this.getServiceList()
    serviceList.forEach((service) => {
      if (queryServiceList.includes(service.value)) {
        sortedService.push(service.value)
      }
    })
    return sortedService
  },

  /**
   * query.eventTypeをconfigの並びに合わせる
   *
   * @param {*} queryEventType
   * @returns
   */
  sortEventType(queryEventType) {
    const sortedEventType = []
    const queryEventTypeList = queryEventType.split(',')
    const facilityEventTypesList = this.getFacilityEventTypesList()
    facilityEventTypesList.forEach((facilityEventType) => {
      if (queryEventTypeList.includes(facilityEventType.value)) {
        sortedEventType.push(facilityEventType.value)
      }
    })
    return sortedEventType
  },

  sortFacilityType(queryFacilityType) {
    const sortedFacilityType = []
    const queryFacilityTypeList = queryFacilityType.split(',')
    const facilityTypeList = this.getFacilityTypeList()
    facilityTypeList.forEach((service) => {
      if (queryFacilityTypeList.includes(service.value)) {
        sortedFacilityType.push(service.value)
      }
    })
    return sortedFacilityType
  },

  /**
   * 検索結果の見出し（メイン、サブ）, meta.title, meta.keywords, meta.description を生成
   *
   * 例）検索見出しメイン：東京都港区の『ピアノ・和室』がある"売店"を含む大人数向け会議室・中ホール・小ホール・レセプションルーム
   * 例）検索見出しサブ：80人・100人以上のセミナー会場、800席・1000席以上の大規模向けイベント会場
   * 例）meta.title：北海道函館市の『和室』がある大人数向け会議室【80人・100人以上のセミナー会場】・大ホール【800席・1000席以上の大規模向けイベント会場】
   * 例）meta.keywords：東京都,港区,ピアノ,和室,売店,大人数向け会議室,中ホール,小ホール,レセプションルーム
   * 例）meta.description：東京都港区の『ピアノ・和室』がある"売店"を含む大人数向け会議室・中ホール・小ホール・レセプションルームのおすすめ一覧です。
   *
   * @param {*} query
   */
  getSearchPageTitle(query) {
    let title = ''
    const keywordsArr = []

    // 施設タイプ取得
    let facilityTypeName = ''
    if (query.facilityType && query.facilityType !== '') {
      const facilityTypeNames = []
      const facilityTypeList = this.getFacilityTypeList()
      query.facilityType.forEach((value) => {
        const name = facilityTypeList.find(
          (facilityType) => facilityType.value === value
        ).name
        facilityTypeNames.push(name)
      })
      facilityTypeName = facilityTypeNames.join('・')
    }

    // 東京都港区の
    if (query.station && query.line) {
      title += query.station + '（' + query.line + '）の'
      keywordsArr.push(query.station)
      keywordsArr.push(query.line)
    } else if (query.station && query.station !== '') {
      title += query.station + 'の'
      keywordsArr.push(query.station)
    } else if (query.city && query.city !== '') {
      title += query.prefecture + query.city + 'の'
      keywordsArr.push(query.prefecture)
      keywordsArr.push(query.city)
    } else if (query.prefecture && query.prefecture !== '') {
      title += query.prefecture + 'の'
      keywordsArr.push(query.prefecture)
    } else if (query.area && query.area !== '') {
      title += query.area + '地方の'
      keywordsArr.push(query.area)
    } else if (!query.eventTag) {
      title += '全国の'
      keywordsArr.push('全国')
    }

    if (facilityTypeName.length > 0) {
      title += facilityTypeName + 'で'
    }

    // 『ピアノ・和室』がある
    if (query.service && query.service.length) {
      const serviceNames = []
      const serviceList = this.getServiceList()
      query.service.forEach((value) => {
        const name = serviceList.find((service) => service.value === value)
          .title
        serviceNames.push(name)
      })

      title += '『' + serviceNames.join('・') + '』がある'
      keywordsArr.push(...serviceNames)
    }

    // "売店"を含む
    if (query.keyword && query.keyword !== '') {
      title += '"' + query.keyword + '"を含む'
      keywordsArr.push(query.keyword)
    }

    // 大人数向け会議室・中ホール・小ホール・レセプションルーム
    const eventTypeFullNames = []
    const eventTypeTitles = []
    const eventTypeSubTitles = []
    if (query.eventType && query.eventType.length) {
      const facilityEventTypesList = this.getFacilityEventTypesList()
      query.eventType.forEach((value) => {
        const facilityEventType = facilityEventTypesList.find(
          (facilityEventType) => facilityEventType.value === value
        )
        eventTypeFullNames.push(
          facilityEventType.searchTitle +
            '【' +
            facilityEventType.searchSubTitle +
            '】'
        )
        eventTypeTitles.push(facilityEventType.searchTitle)
        eventTypeSubTitles.push(facilityEventType.searchSubTitle)
      })
    }
    // 検索見出しとmeta.titleで値が違うため、別途変数を宣言
    let searchTitle = title

    if (query.eventTag) {
      searchTitle += query.eventTag + 'イベントにおすすめの'
      title += query.eventTag + 'イベントにおすすめの'
      keywordsArr.push(query.eventTag)
    }

    // meta.titleのeventType部分をセット
    if (eventTypeFullNames.length) {
      if (!query.eventTag) {
        title += '人気の'
      }
      title += eventTypeFullNames.join('・')
      if (query.eventTag) {
        title += 'まとめ'
      }
    } else if (query.eventTag) {
      title += 'セミナー会場まとめ'
    } else {
      title += '人気の格安セミナー会場（貸し会議室・ホール・レンタルスペース）'
    }

    // 検索見出しメインのeventType部分をセット
    if (eventTypeTitles.length) {
      if (!query.eventTag) {
        searchTitle += '人気の'
      }
      searchTitle += eventTypeTitles.join('・')
      keywordsArr.push(...eventTypeTitles)

      if (query.eventTag) {
        searchTitle += 'まとめ'
      }
    } else if (query.eventTag) {
      searchTitle += 'セミナー会場まとめ'
    } else {
      searchTitle +=
        '人気の格安セミナー会場（貸し会議室・ホール・レンタルスペース）'
    }

    // 検索見出しサブをセット
    let searchSubTitle = ''
    if (eventTypeSubTitles.length) {
      searchSubTitle = eventTypeSubTitles.join('、')
    }

    // meta.keywords, meta.description をセット
    const keywords = keywordsArr.join(',') + ',' + process.env.SITE_KEYWORDS
    const description =
      title + 'のおすすめ一覧です。' + process.env.SITE_DESCRIPTION

    return { searchTitle, searchSubTitle, title, keywords, description }
  },
  /**
   * query parameterにnocache=1があるかどうか判定
   * @param {object} query
   * @returns bool
   */
  isNoCacheQuery(query) {
    let ret = 0
    if (
      query !== null &&
      typeof query === 'object' &&
      Object.prototype.toString.call(query) === '[object Object]'
    ) {
      if (query.nocache && query.nocache === '1') {
        ret = 1
      }
    }
    return ret
  },

  /**
   * InternetExplorerからのアクセスかどうか
   *
   * @returns
   */
  isIe() {
    let ret = false
    if (process.client) {
      const userAgent = window.navigator.userAgent.toLowerCase()
      if (userAgent.includes('trident')) {
        ret = true
      }
    }
    return ret
  },

  /**
   * swiperOptionsを返す
   *
   * @param {number} [size=3]
   * @returns
   */
  swiperOption(size = 3) {
    return {
      // Disable preloading of all images
      preloadImages: false,
      // Enable lazy loading
      lazy: {
        loadPrevNext: true
      },
      loop: false,
      spaceBetween: 20,
      slidesPerView: size,
      pagination: {
        el: '.swiper-pagination'
      },
      navigation: {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev'
      },
      autoplay: false,
      breakpoints: {
        1024: {
          slidesPerView: 2
        },
        768: {
          slidesPerView: 1
        }
      }
    }
  },
  /**
   * solr facility.stationsから駅名と路線名に分割する
   * @param {string} name
   * @returns object
   */
  getStationAndLineName(name) {
    const reg = /(.+)（(.+)）/g
    const st = name.replace(reg, '$1')
    const li = name.replace(reg, '$2')
    return {
      station: st,
      line: li
    }
  }
}
